export type PwaInstallOutcome = "accepted" | "dismissed" | "unavailable";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type PwaInstallSnapshot = {
  installed: boolean;
  canPrompt: boolean;
  ios: boolean;
  mobile: boolean;
};

const listeners = new Set<() => void>();
let promptEvent: InstallPromptEvent | undefined;
let initialized = false;
let snapshot: PwaInstallSnapshot = {
  installed: false,
  canPrompt: false,
  ios: false,
  mobile: false,
};

type MobileAwareNavigator = Navigator & {
  standalone?: boolean;
  userAgentData?: { mobile?: boolean };
};

function isStandalone() {
  const appleNavigator = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches ||
    appleNavigator.standalone === true;
}

function isIosDevice() {
  const appleNavigator = navigator as MobileAwareNavigator;
  return /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" &&
      (appleNavigator.maxTouchPoints ?? 0) > 1);
}

export function isMobileInstallPlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return false;
  const mobileNavigator = navigator as MobileAwareNavigator;
  if (mobileNavigator.userAgentData?.mobile === true) return true;
  if (/Android|iPad|iPhone|iPod/i.test(navigator.userAgent)) return true;
  if (isIosDevice()) return true;
  return window.matchMedia("(pointer: coarse)").matches &&
    Math.min(window.screen.width, window.screen.height) <= 767;
}

function attachMobileManifest() {
  if (document.querySelector('link[rel="manifest"]')) return;
  const manifest = document.createElement("link");
  manifest.rel = "manifest";
  manifest.href = "/manifest.webmanifest";
  document.head.append(manifest);
}

function publish(next: PwaInstallSnapshot) {
  if (
    next.installed === snapshot.installed &&
    next.canPrompt === snapshot.canPrompt &&
    next.ios === snapshot.ios &&
    next.mobile === snapshot.mobile
  ) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

export function initializePwaInstall() {
  if (initialized) return snapshot.mobile;
  initialized = true;
  const mobile = isMobileInstallPlatform();
  if (!mobile) {
    publish({ installed: false, canPrompt: false, ios: false, mobile: false });
    return false;
  }
  attachMobileManifest();
  publish({ installed: isStandalone(), canPrompt: false, ios: isIosDevice(), mobile: true });
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    promptEvent = event as InstallPromptEvent;
    publish({ ...snapshot, canPrompt: true });
  });
  window.addEventListener("appinstalled", () => {
    promptEvent = undefined;
    publish({ ...snapshot, installed: true, canPrompt: false });
  });
  return true;
}

export function getPwaInstallSnapshot() {
  return snapshot;
}

export function subscribePwaInstall(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function requestPwaInstall(): Promise<PwaInstallOutcome> {
  const currentPrompt = promptEvent;
  if (!snapshot.mobile || !currentPrompt || snapshot.installed)
    return "unavailable";
  promptEvent = undefined;
  publish({ ...snapshot, canPrompt: false });
  await currentPrompt.prompt();
  const choice = await currentPrompt.userChoice;
  if (choice.outcome === "accepted")
    publish({ ...snapshot, installed: true });
  return choice.outcome;
}
